import { apiCollectionToRowResponse, rowQueryToApiParams } from "../modules/tableQuery";
import { renderTableLink } from "../components/renderTableCell";

import { AdminContainer } from "../components/layouts";
import { Chip, Tooltip, Checkbox, FormControlLabel, Button } from "@material-ui/core";
import MaterialTable from "material-table";
import React from "react";
import api from "../api";
import tableIcons from "../components/tableIcons";
import { useTheme } from "@material-ui/core/styles";

export const ChimeVerifiedEnrollmentsListPage = () => {
  const tableRef = React.createRef();
  const theme = useTheme();

  return (
    <AdminContainer variant="list">
      <MaterialTable
        title="Chime Verifications"
        tableRef={tableRef}
        columns={makeColumns(theme)}
        options={{
          debounceInterval: 500,
          emptyRowsWhenPaging: false,
          filtering: true,
          search: true,
          searchFieldAlignment: "left",
          pageSize: 50,
          pageSizeOptions: [25, 50, 100],
          sorting: true,
          thirdSortClick: false,
          filterCellStyle: {
            transform: "scale(0.90)",
            transformOrigin: "left",
          },
        }}
        initialState={{
          filters: [
            {
              column: "treatment",
              value: ["needs_treatment"]
            }
          ]
        }}
        data={(q) => {
          // Get standard parameters
          const params = rowQueryToApiParams(q);

          // Map field names for backend compatibility
          if (q.filters) {
            params.filters = params.filters.map(filter => {
              // Convert camelCase to snake_case for known fields
              const fieldMap = {
                'bypassedByClassifier': 'bypassed_by_classifier',
                'hasPendingRentalVerifications': 'has_pending_rental_verifications',
                'leaseLatestStatus': 'lease_latest_status',
                'landlordLatestStatus': 'landlord_latest_status'
              };

              return {
                ...filter,
                name: fieldMap[filter.name] || filter.name
              };
            });
          }

          // Add explicit sorting parameters if needed
          if (q.orderBy) {
            const orderByMap = {
              'customerName': 'customer_name',
              'leaseLatestStatus': 'lease_latest_status',
              'landlordLatestStatus': 'landlord_latest_status',
              'bypassedByClassifier': 'bypassed_by_classifier',
              'hasPendingRentalVerifications': 'has_pending_rental_verifications'
            };

            params.order_by = orderByMap[q.orderBy.field] || q.orderBy.field;
            params.order_direction = q.orderDirection;
          }

          return api
            .listChimeEnrollments(params)
            .then((resp) => apiCollectionToRowResponse(resp));
        }}
        icons={tableIcons}
      />
    </AdminContainer>
  );
};

function makeColumns(theme) {
  // Map specific statuses to user-friendly labels
  function getStatusInfo(status) {
    if (!status) return { label: "Not Started", color: "default" };

    const statusMap = {
      "lease_verification_verified": { label: "Verified", color: "primary" },
      "lease_verification_rejected": { label: "Rejected", color: "secondary" },
      "lease_verification_pending": { label: "Pending", color: "default" },
      "landlord_verification_verified": { label: "Verified", color: "primary" },
      "landlord_verification_rejected": { label: "Rejected", color: "secondary" },
      "landlord_verification_pending": { label: "Pending", color: "default" },
      "approved": { label: "Approved", color: "primary" },
      "rejected": { label: "Rejected", color: "secondary" },
      "submitted": { label: "Submitted", color: "default" },
      "pending": { label: "Pending", color: "default" },
    };

    return statusMap[status] || { label: status, color: "default" };
  }

  function renderVerificationStatus(status) {
    if (!status) return <Chip size="small" label="Not Started" />;
    const { label, color } = getStatusInfo(status);
    return <Chip size="small" color={color} label={label} />;
  }

  function renderNeedsTreatmentIndicator(rowData) {
    if (!rowData.needsTreatment) return null;

    return (
      <Tooltip title="Needs Treatment">
        <Chip
          size="small"
          color="secondary"
          label="!"
          style={{
            borderRadius: "50%",
            width: 24,
            height: 24,
            fontWeight: "bold",
            marginLeft: 5,
          }}
        />
      </Tooltip>
    );
  }

  // Helper function for checkbox filters
  function createCheckboxFilter(label, filterValue) {
    return props => (
      <div style={{ padding: "10px 0" }}>
        <Tooltip title={`Show only ${label}`}>
          <FormControlLabel
            control={
              <Checkbox
                checked={props.columnDef.tableData.filterValue &&
                  props.columnDef.tableData.filterValue.includes(filterValue)}
                onChange={(e) => {
                  props.onFilterChanged(props.columnDef.tableData.id,
                    e.target.checked ? [filterValue] : []);
                }}
              />
            }
            label={`${label}`}
          />
        </Tooltip>
      </div>
    );
  }

  return [
    {
      title: "ID",
      field: "id",
      defaultSort: "desc",
      filtering: false,
      width: 70,
      render: renderTableLink({
        url: "/reporting-account-enrollments/",
        id: "id",
        body: (rowData) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            {rowData.id}
            {renderNeedsTreatmentIndicator(rowData)}
          </div>
        )
      }),
    },
    {
      title: "Customer",
      field: "customerName",
      filtering: false,
      render: renderTableLink({
        url: "/customers/",
        id: "customerId",
        body: "customerName",
      }),
    },
    {
      title: "Email",
      field: "email",
      filtering: false,
      width: 180,
      render: (rowData) => (
        <a href={`mailto:${rowData.email}`} target="_blank" rel="noopener noreferrer">
          {rowData.email}
        </a>
      )
    },
    {
      title: "Status",
      field: "status",
      filtering: false,
      width: 90,
      render: (rowData) => renderVerificationStatus(rowData.status),
    },
    {
      title: "Lease Verification",
      field: "leaseLatestStatus",
      filtering: true,
      filterComponent: createCheckboxFilter("Pending", "pending"),
      render: (rowData) => {
        const lease = rowData.leaseVerification;

        if (!lease || !lease.id) {
          return <Chip size="small" label="Not Started" />;
        }

        const statusInfo = getStatusInfo(lease.status);

        return (
          <div>
            <Tooltip title="Open lease verification">
              <Button
                variant="contained"
                size="small"
                color={statusInfo.color}
                onClick={() => window.open(`/enrollment-verifications/${lease.id}`, '_blank')}
                style={{
                  minWidth: '80px',
                  textTransform: 'none'
                }}
              >
                {statusInfo.label}
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Landlord Verification",
      field: "landlordLatestStatus",
      filtering: true,
      filterComponent: createCheckboxFilter("Pending", "pending"),
      render: (rowData) => {
        const landlord = rowData.landlordVerification;

        if (!landlord || !landlord.id) {
          return <Chip size="small" label="Not Started" />;
        }

        const statusInfo = getStatusInfo(landlord.status);

        return (
          <div>
            <Tooltip title="Open landlord verification">
              <Button
                variant="contained"
                size="small"
                color={statusInfo.color}
                onClick={() => window.open(`/enrollment-verifications/${landlord.id}`, '_blank')}
                style={{
                  minWidth: '80px',
                  textTransform: 'none'
                }}
              >
                {statusInfo.label}
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Payment verification",
      field: "hasPendingRentalVerifications",
      filtering: true,
      width: 120,
      filterComponent: createCheckboxFilter("Has Pending RVs", "true"),
      render: (rowData) => (
        <div>
          <Tooltip title="Rental Verifications">
            <Button
              variant="contained"
              size="small"
              color={rowData.hasPendingRentalVerifications ? "secondary" : "default"}
              onClick={() => window.open(`/reporting-account-enrollments/${rowData.id}/chime-payment-verifications`, '_blank')}
              style={{
                minWidth: '80px',
                boxShadow: rowData.hasPendingRentalVerifications ? '0 2px 5px rgba(0,0,0,0.2)' : 'none',
                textTransform: 'none'
              }}
            >
              {rowData.hasPendingRentalVerifications ? "Yes" : "No"}
            </Button>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Needs Treatment",
      field: "treatment",
      width: 120,
      filterComponent: createCheckboxFilter("Needs Treatment", "needs_treatment"),
      render: (rowData) => (
        <Chip
          size="small"
          color={rowData.needsTreatment ? "secondary" : "default"}
          label={rowData.needsTreatment ? "Yes" : "No"}
        />
      ),
      defaultFilter: ["needs_treatment"]
    },
    {
      title: "Bypassed",
      field: "bypassedByClassifier",
      filtering: true,
      width: 90,
      filterComponent: createCheckboxFilter("Bypassed", "true"),
      render: (rowData) => (
        <div>
          {rowData.bypassedByClassifier ? (
            <Chip
              size="small"
              color="primary"
              label="Yes"
            />
          ) : (
            <Chip
              size="small"
              color="default"
              label="No"
            />
          )}
        </div>
      ),
    },
  ];
}